import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { AuthService } from '../services/auth.service';
import { CompanyVendorService } from '../services/company-vendor.service';

@Injectable({
  providedIn: 'root'
})
export class RowcalTermsGuard {
  constructor(
    private authService: AuthService,
    private companyVendorService: CompanyVendorService,
    private router: Router
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    // Check if user is authenticated first
    if (!this.authService.isAuthenticated()) {
      this.router.navigate(['login'], {
        queryParams: { returnUrl: state.url }
      });
      return of(false);
    }

    // Get the required company ID from route data
    const requiredCompanyId = route.data['companyId'];

    // Get the user type
    return this.authService.getUserType().pipe(
      switchMap((userType) => {
        // Allow access for Admin users
        if (userType === 'Admin') {
          return of(true);
        }

        // For Vendor users, check if they're registered under the specified company
        if (userType === 'Vendor') {
          return this.checkVendorCompanyRegistration(requiredCompanyId);
        }

        // Deny access for all other user types
        this.router.navigate(['login']);
        return of(false);
      }),
      catchError((error) => {
        console.error('RowcalTermsGuard error:', error);
        this.router.navigate(['login']);
        return of(false);
      })
    );
  }

  private checkVendorCompanyRegistration(
    companyId?: number
  ): Observable<boolean> {
    // If no company ID is provided, deny access
    if (!companyId) {
      this.router.navigate(['login']);
      return of(false);
    }

    try {
      const vendorId = this.authService.getUserVendorId();

      if (!vendorId) {
        this.router.navigate(['login']);
        return of(false);
      }

      // Fetch the CompanyVendor relationship from the API
      return this.companyVendorService
        .getCompanyVendor({
          VendorId: vendorId,
          CompanyId: companyId
        })
        .pipe(
          map((companyVendor) => {
            // If a CompanyVendor relationship exists, allow access
            if (companyVendor && companyVendor.id) {
              return true;
            }
            // No relationship found, deny access
            this.router.navigate(['login']);
            return false;
          }),
          catchError((error) => {
            console.error('Error checking vendor registration:', error);
            this.router.navigate(['login']);
            return of(false);
          })
        );
    } catch (error) {
      console.error('Error in checkVendorCompanyRegistration:', error);
      this.router.navigate(['login']);
      return of(false);
    }
  }
}
